import { injector } from "../../../dependencies/injector";
import { PropertyConfig } from "../../../forms/propertyConfig";
import { ValidationError } from "../../../forms/validationError";
import { getProp } from "../../../tools/any.tools";
import { LOG_SERVICE, LogService } from "../../log.service";
import { VALIDATION_SERVICE, ValidationService } from "../../validation.service";

/** constant used for complex validations */
export const EDITED_DATA_IDS: string = "editedDataIds";

export interface Validator<Value = unknown, Params = unknown> {
    /**
     * Check if editor value satisfy validator constraints
     */
    isValid(property: PropertyConfig, propertyValue: Value, validatorParams: Params, allProperties: Record<string, PropertyConfig>, allPropertiesValue: Record<string, unknown>): boolean;

    /**
     * Generates an error message
     * ! this function does not need to be defined on simple validators, you simply need to register the error message on the validation service
     * This function can set the isWarning field of the returned value to override the error level of the validator on a case-by-case basis.
     *
     */
    getError?(lang: string, property: PropertyConfig, propertyValue: Value, validatorParams: Params, allProperties: Record<string, PropertyConfig>, allPropertiesValue: Record<string, unknown>): ValidationError;

    /**
     * isWarning: if true, use validator only as a warning (display warning message, but consider validation is ok)
     * The default value of the validator can be overridden on a case-by-case basis in the getError() result.
     */
    isWarning?: boolean;
}

const VALIDATOR_NAME_CLASS_PROPERTY = "_validatorName";
/**
 * RegisterValidator class Decorator for classes that implement Validator
 * 
 * This decorator registers the validator on the ValidationService and saves the validator name on the class prototype so that we can use it in validatorEntry 
 * @param validatorName the name of the validator that will be registered
 */
export function RegisterValidator(validatorName: string): (Constructor: new () => Validator) => void {
    return (Constructor: new () => Validator): void => {
        // register validator name on the class prototype
        Constructor.prototype[VALIDATOR_NAME_CLASS_PROPERTY] = validatorName;

        // add validator on the ValidationService
        const validationService = injector.find<ValidationService>(VALIDATION_SERVICE);
        if (validationService) {
            validationService.addValidator(validatorName, new Constructor());
        }
        injector.addRegisterDependencyListener<ValidationService>(VALIDATION_SERVICE, (validationService) => {
            // when a validation service is registered, we add this validator
            validationService.addValidator(validatorName, new Constructor(), { overrideIfAlreadyPresent: false, doNotDisplayErrorIfAlreadyPresent: true });
        });
    };
}

/**
 * @returns the validator name provided by the @RegisterValidator that decorates the validatorClass
 */
export function getValidatorName<V extends Validator>(validatorClassOrInstance: (new () => V) | V): string | undefined {
    if ("prototype" in validatorClassOrInstance) {
        // is a class
        const validatorName = validatorClassOrInstance.prototype[VALIDATOR_NAME_CLASS_PROPERTY];
        if (!validatorName) {
            injector.find<LogService>(LOG_SERVICE)?.error(VALIDATOR_NAME_CLASS_PROPERTY, "was not found on", validatorClassOrInstance, "is the class decorated with RegisterValidator ?");
        }
        return validatorName;
    }
    // is an instance
    return validatorClassOrInstance ? getProp(validatorClassOrInstance, VALIDATOR_NAME_CLASS_PROPERTY) : undefined;
}

/**
 * This function is a convenient way to ensure we create a validator entry that respects the validator expected params and the validator name
 * If not validatorName is provided, the validator name provided to @RegisterValidator is used
 */
export function validatorEntry<Value, Params, V extends Validator<Value, Params>>(validatorClass: new () => V, params: Params, validatorName?: string): { [validatorName: string]: Params } {
    if (!validatorName) {
        // if no validator name is provided, we take the validation name that was provided on @RegisterValidator
        validatorName = getValidatorName(validatorClass);
    }
    if (!validatorName) {
        return {};
    }
    return { [validatorName]: params };
}